import { useState, useEffect, useCallback, useMemo } from 'react';
import { api } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { CurrencyEur, Upload, X, CaretLeft, CaretRight, Receipt, CheckSquare, Printer } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { formatCurrency, formatDate } from './formatters';

// Periodi fissi: 15/09, 15/12, 15/02(+1) rispetto all'anno accademico selezionato
function getPeriodiForAnno(anno) {
  if (!anno) return [];
  const y = parseInt(anno);
  return [
    { key: '1', label: `Periodo 1 · Scad 15/09/${y}`, date: `${y}-09-15` },
    { key: '2', label: `Periodo 2 · Scad 15/12/${y}`, date: `${y}-12-15` },
    { key: '3', label: `Periodo 3 · Scad 15/02/${y + 1}`, date: `${y + 1}-02-15` },
  ];
}

export function AddebitiSection({ teachers, anno, onImport }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [dataEsportazione, setDataEsportazione] = useState('');
  const [availableAnniCompetenza, setAvailableAnniCompetenza] = useState([]);
  const [filterTeacherId, setFilterTeacherId] = useState('');
  const [filterStudentName, setFilterStudentName] = useState('');
  const [filterAnnoCompetenza, setFilterAnnoCompetenza] = useState('');
  const [filterPeriodo, setFilterPeriodo] = useState('');
  const [sortBy, setSortBy] = useState('');
  const [totals, setTotals] = useState({
    total_addebiti_listino: 0,
    total_dovuto_addebiti: 0, total_pagato_addebiti: 0, total_da_pagare_addebiti: 0,
    total_dovuto_alunno: 0, total_riscosso_alunno: 0, total_da_riscuotere_alunno: 0,
  });
  // Selezione + Assegna Ricevuta — persistente in localStorage con snapshot degli importi
  const SELECTION_STORAGE_KEY = 'addebiti_selected_v1';
  const [selectedMap, setSelectedMap] = useState(() => {
    try {
      const raw = localStorage.getItem(SELECTION_STORAGE_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch { return {}; }
  });
  const [nrRicevuta, setNrRicevuta] = useState('');
  const [dataRicevuta, setDataRicevuta] = useState(new Date().toISOString().slice(0, 10));
  const [assegnando, setAssegnando] = useState(false);

  // Persisti selezioni su ogni cambio
  useEffect(() => {
    try { localStorage.setItem(SELECTION_STORAGE_KEY, JSON.stringify(selectedMap)); } catch {}
  }, [selectedMap]);

  const selectedIds = useMemo(() => Object.keys(selectedMap), [selectedMap]);
  const selectedCount = selectedIds.length;
  const selectedTotals = useMemo(() => {
    return Object.values(selectedMap).reduce((acc, s) => ({
      importo: acc.importo + (Number(s.importo) || 0),
      importo_insegnante: acc.importo_insegnante + (Number(s.importo_insegnante) || 0),
    }), { importo: 0, importo_insegnante: 0 });
  }, [selectedMap]);

  const periodi = useMemo(() => getPeriodiForAnno(filterAnnoCompetenza && filterAnnoCompetenza !== 'all' ? filterAnnoCompetenza : anno), [filterAnnoCompetenza, anno]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 50 };
      if (filterTeacherId) params.teacher_id = filterTeacherId;
      if (anno) params.anno = anno;
      if (filterStudentName) params.student_name = filterStudentName;
      if (filterAnnoCompetenza && filterAnnoCompetenza !== 'all') params.anno_competenza = filterAnnoCompetenza;
      if (filterPeriodo && filterPeriodo !== 'all') {
        const p = periodi.find(x => x.key === filterPeriodo);
        if (p) params.data_scadenza = p.date;
      }
      if (sortBy) params.sort_by = sortBy;
      const response = await api.getAdminPayments(params);
      const d = response.data;
      setRows(d.records);
      setTotal(d.total);
      setPages(d.pages);
      if (d.totals) setTotals(d.totals);
      if (d.data_esportazione !== undefined) setDataEsportazione(d.data_esportazione || '');
      if (d.available_anni_competenza) setAvailableAnniCompetenza(d.available_anni_competenza);
    } catch { toast.error('Errore nel caricamento addebiti'); }
    finally { setLoading(false); }
  }, [page, filterTeacherId, filterStudentName, filterAnnoCompetenza, filterPeriodo, sortBy, anno, periodi]);

  useEffect(() => { load(); }, [load]);
  // I filtri resettano la pagina ma NON le selezioni (persistenti in localStorage)
  useEffect(() => { setPage(1); }, [filterTeacherId, filterStudentName, filterAnnoCompetenza, filterPeriodo, sortBy]);

  // Aggiorna gli snapshot degli importi per le righe attualmente visibili (in caso di modifiche)
  useEffect(() => {
    if (!rows.length) return;
    setSelectedMap(prev => {
      let changed = false;
      const next = { ...prev };
      rows.forEach(r => {
        if (next[r.id]) {
          const imp = Number(r.importo) || 0;
          const impIns = Number(r.importo_insegnante) || 0;
          if (next[r.id].importo !== imp || next[r.id].importo_insegnante !== impIns) {
            next[r.id] = { importo: imp, importo_insegnante: impIns };
            changed = true;
          }
        }
      });
      return changed ? next : prev;
    });
  }, [rows]);

  const hasFilters = filterTeacherId || filterStudentName || (filterAnnoCompetenza && filterAnnoCompetenza !== 'all') || (filterPeriodo && filterPeriodo !== 'all');
  const clearFilters = () => { setFilterTeacherId(''); setFilterStudentName(''); setFilterAnnoCompetenza(''); setFilterPeriodo(''); setPage(1); };

  const canEditDovuto = (p) => String(p.id_causale || '') !== '1' && !!p.data_pagamento;
  const eligibleRows = useMemo(() => rows.filter(canEditDovuto), [rows]);

  const isSelected = (id) => !!selectedMap[id];
  const toggleSelect = (p) => setSelectedMap(prev => {
    const next = { ...prev };
    if (next[p.id]) delete next[p.id];
    else next[p.id] = { importo: Number(p.importo) || 0, importo_insegnante: Number(p.importo_insegnante) || 0 };
    return next;
  });
  const toggleSelectAll = () => {
    const eligibleIds = eligibleRows.map(r => r.id);
    const allSelected = eligibleIds.length > 0 && eligibleIds.every(id => !!selectedMap[id]);
    setSelectedMap(prev => {
      const next = { ...prev };
      if (allSelected) {
        eligibleIds.forEach(id => delete next[id]);
      } else {
        eligibleRows.forEach(r => { next[r.id] = { importo: Number(r.importo) || 0, importo_insegnante: Number(r.importo_insegnante) || 0 }; });
      }
      return next;
    });
  };
  const resetSelezioni = () => {
    if (selectedCount === 0) return;
    if (!window.confirm(`Vuoi azzerare le ${selectedCount} selezioni salvate?`)) return;
    setSelectedMap({});
    toast.success('Selezioni azzerate');
  };

  // Stampa tabulato degli addebiti selezionati (recupera dettagli via API)
  const [printing, setPrinting] = useState(false);
  const handlePrint = async () => {
    if (selectedCount === 0) { toast.error('Nessun addebito selezionato'); return; }
    setPrinting(true);
    try {
      const res = await api.getAdminPaymentsByIds(selectedIds);
      const { records, totals } = res.data || { records: [], totals: {} };
      if (!records.length) { toast.error('Nessun addebito trovato'); return; }
      const printWin = window.open('', '_blank', 'width=1000,height=800');
      if (!printWin) { toast.error('Impossibile aprire la finestra di stampa (popup bloccato?)'); return; }
      const fmtEur = (v) => new Intl.NumberFormat('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v || 0);
      const fmtDate = (iso) => {
        if (!iso) return '-';
        try {
          const d = new Date(iso);
          if (isNaN(d)) return String(iso);
          return d.toLocaleDateString('it-IT');
        } catch { return String(iso); }
      };
      const today = new Date().toLocaleDateString('it-IT');
      const rowsHtml = records.map((r, i) => `
        <tr>
          <td class="c">${i + 1}</td>
          <td class="c mono">${r.codice_alunno || '-'}</td>
          <td>${(r.student_name || '').toUpperCase()}</td>
          <td>${(r.teacher_name || '').toUpperCase()}</td>
          <td>${r.descrizione || '-'}</td>
          <td class="c">${fmtDate(r.data_pagamento)}</td>
          <td class="r mono">${fmtEur(r.importo)}</td>
          <td class="r mono">${fmtEur(r.importo_insegnante)}</td>
          <td class="r mono">${fmtEur(r.pagato_insegnante)}</td>
          <td class="c mono">${r.nr_ricevuta_ins || '-'}</td>
          <td class="c">${fmtDate(r.data_ricevuta_ins)}</td>
        </tr>
      `).join('');
      const html = `<!DOCTYPE html><html lang="it"><head><meta charset="utf-8"/><title>Tabulato Addebiti — ${today}</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif; padding: 20mm 15mm; color: #1f1f28; }
  h1 { margin: 0 0 4px 0; font-size: 20px; color: #2D2A4A; }
  .sub { color: #52525B; font-size: 11px; margin-bottom: 16px; }
  table { width: 100%; border-collapse: collapse; font-size: 10px; }
  th { background: #2D2A4A; color: #fff; text-align: left; padding: 6px 5px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.4px; font-size: 9px; }
  td { padding: 4px 5px; border-bottom: 1px solid #E4E4E7; vertical-align: top; }
  tr:nth-child(even) td { background: #FAFAFA; }
  .c { text-align: center; }
  .r { text-align: right; }
  .mono { font-family: "SF Mono", Menlo, Consolas, monospace; }
  tfoot td { font-weight: 700; background: #EEF2FF !important; border-top: 2px solid #2D2A4A; font-size: 11px; padding: 8px 5px; }
  .totals { color: #4338CA; }
  .footer-note { margin-top: 20px; font-size: 10px; color: #71717A; text-align: right; }
  @media print { body { padding: 10mm; } .no-print { display: none; } }
  .toolbar { position: fixed; top: 8px; right: 12px; background: #fff; }
  .toolbar button { background: #2D2A4A; color: #fff; border: 0; padding: 8px 14px; font-weight: 600; cursor: pointer; margin-left: 6px; border-radius: 3px; }
  .toolbar button.secondary { background: #71717A; }
</style></head>
<body>
  <div class="toolbar no-print">
    <button class="secondary" onclick="window.close()">Chiudi</button>
    <button onclick="window.print()">Stampa</button>
  </div>
  <h1>Tabulato Addebiti Selezionati</h1>
  <div class="sub">Stampa del ${today} · ${records.length} ${records.length === 1 ? 'addebito' : 'addebiti'} selezionati</div>
  <table>
    <thead>
      <tr>
        <th>#</th>
        <th>Cod. Alunno</th>
        <th>Alunno</th>
        <th>Insegnante</th>
        <th>Descrizione</th>
        <th>Pagamento</th>
        <th style="text-align:right">Importo</th>
        <th style="text-align:right">Dovuto Ins.</th>
        <th style="text-align:right">Pagato Ins.</th>
        <th>Nr. Ricevuta</th>
        <th>Data Ric.</th>
      </tr>
    </thead>
    <tbody>${rowsHtml}</tbody>
    <tfoot>
      <tr>
        <td colspan="6" class="r">TOTALI</td>
        <td class="r mono totals">€ ${fmtEur(totals.importo)}</td>
        <td class="r mono totals">€ ${fmtEur(totals.importo_insegnante)}</td>
        <td class="r mono totals">€ ${fmtEur(totals.pagato_insegnante)}</td>
        <td colspan="2"></td>
      </tr>
    </tfoot>
  </table>
  <div class="footer-note">Documento generato da Fiori Musicali · ${today}</div>
  <script>window.onload = function() { setTimeout(function() { window.print(); }, 300); };</script>
</body></html>`;
      printWin.document.open();
      printWin.document.write(html);
      printWin.document.close();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore stampa');
    } finally {
      setPrinting(false);
    }
  };

  const updatePayment = async (id, patch) => {
    try {
      await api.updateAdminPayment(id, patch);
      setRows(prev => prev.map(x => x.id === id ? { ...x, ...patch } : x));
      toast.success('Aggiornato');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore aggiornamento');
      load();
    }
  };

  const assegnaRicevuta = async () => {
    if (!selectedCount) { toast.error('Seleziona almeno un addebito'); return; }
    if (!nrRicevuta.trim()) { toast.error('Inserisci il numero ricevuta'); return; }
    if (!dataRicevuta) { toast.error('Inserisci la data ricevuta'); return; }
    if (!window.confirm(`Confermi di assegnare la ricevuta nr. ${nrRicevuta} del ${dataRicevuta} a ${selectedCount} addebiti?\nIl valore "Dovuto Ins." sarà copiato in "Pagato Ins.".`)) return;
    setAssegnando(true);
    try {
      const res = await api.assegnaRicevutaPayments(selectedIds, nrRicevuta.trim(), dataRicevuta);
      const { updated, skipped } = res.data || {};
      toast.success(`Ricevuta assegnata a ${updated} addebiti${skipped ? ` (${skipped} saltati)` : ''}`);
      setSelectedMap({}); setNrRicevuta('');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore assegnazione ricevuta');
    } finally {
      setAssegnando(false);
    }
  };

  return (
    <>
      <div className="mb-6 animate-fade-in flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold font-['Work_Sans'] tracking-tight mb-2">Addebiti</h1>
          <p className="text-[#52525B] text-sm">Lista completa degli addebiti importati da Excel</p>
          {dataEsportazione && (
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#2D2A4A] text-white rounded-sm mt-2" data-testid="addebiti-data-esportazione">
              <span className="text-sm font-semibold tracking-wide">Ultima importazione: {formatDate(dataEsportazione)}</span>
            </div>
          )}
        </div>
        <Button onClick={onImport} className="bg-[#0A0A0A] rounded-sm" data-testid="addebiti-import-btn">
          <Upload size={16} className="mr-2" />Importa Addebiti
        </Button>
      </div>

      {/* Riga 1 — Totali ALUNNO */}
      <div className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-[#52525B]">Riepilogo Alunno</div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4 animate-fade-in animate-delay-1">
        <div className="bg-white border border-[#E4E4E7] rounded-sm p-4" data-testid="addebiti-total-dovuto-alunno">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-[#52525B] mb-1">Totale Dovuto Alunno</div>
          <div className="text-xl font-bold font-['Work_Sans']">{formatCurrency(totals.total_dovuto_alunno)}</div>
        </div>
        <div className="bg-green-50 border border-green-200 rounded-sm p-4" data-testid="addebiti-total-riscosso-alunno">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-green-700 mb-1">Totale Riscosso Alunno</div>
          <div className="text-xl font-bold font-['Work_Sans'] text-green-800">{formatCurrency(totals.total_riscosso_alunno)}</div>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-sm p-4" data-testid="addebiti-total-da-riscuotere-alunno">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-amber-700 mb-1">Totale da Riscuotere Alunno</div>
          <div className="text-xl font-bold font-['Work_Sans'] text-amber-800">{formatCurrency(totals.total_da_riscuotere_alunno)}</div>
        </div>
      </div>

      {/* Riga 2 — Totali INSEGNANTE */}
      <div className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-[#52525B]">Riepilogo Insegnante</div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5 animate-fade-in animate-delay-1">
        <div className="bg-[#EEF2FF] border border-[#C7D2FE] rounded-sm p-4" data-testid="addebiti-total-dovuto">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-[#4338CA] mb-1">Totale Dovuto Insegnante</div>
          <div className="text-xl font-bold font-['Work_Sans'] text-[#4338CA]">{formatCurrency(totals.total_dovuto_addebiti)}</div>
        </div>
        <div className="bg-green-50 border border-green-200 rounded-sm p-4" data-testid="addebiti-total-pagato">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-green-700 mb-1">Totale Pagato Insegnante</div>
          <div className="text-xl font-bold font-['Work_Sans'] text-green-800">{formatCurrency(totals.total_pagato_addebiti)}</div>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-sm p-4" data-testid="addebiti-total-da-pagare">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-amber-700 mb-1">Totale da Pagare Insegnante</div>
          <div className="text-xl font-bold font-['Work_Sans'] text-amber-800">{formatCurrency(totals.total_da_pagare_addebiti)}</div>
        </div>
      </div>

      <div className="flex items-center gap-3 mb-3 flex-wrap animate-fade-in animate-delay-1">
        <Select value={filterTeacherId} onValueChange={setFilterTeacherId}>
          <SelectTrigger className={`w-full sm:w-[250px] rounded-sm h-10 text-sm font-semibold border-2 ${filterTeacherId ? 'border-[#2D2A4A] bg-[#2D2A4A] text-white' : 'border-[#2D2A4A] text-[#2D2A4A]'}`} data-testid="filter-addebiti-teacher"><SelectValue placeholder="Tutti gli insegnanti" /></SelectTrigger>
          <SelectContent>{teachers.map(t => <SelectItem key={t.id} value={t.id}>{t.name?.toUpperCase()}</SelectItem>)}</SelectContent>
        </Select>
        <Input type="text" placeholder="Cerca alunno..." value={filterStudentName} onChange={(e) => setFilterStudentName(e.target.value)} className="w-full sm:w-[200px] rounded-sm h-9 text-sm uppercase" data-testid="filter-addebiti-student" />
        <Select value={filterAnnoCompetenza} onValueChange={setFilterAnnoCompetenza}>
          <SelectTrigger className="w-full sm:w-[160px] rounded-sm h-9 text-sm" data-testid="filter-addebiti-anno-competenza"><SelectValue placeholder="Anno Competenza" /></SelectTrigger>
          <SelectContent><SelectItem value="all">Tutti gli anni</SelectItem>{availableAnniCompetenza.map(a => <SelectItem key={a} value={String(a)}>{a}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={filterPeriodo || 'all'} onValueChange={(v) => setFilterPeriodo(v === 'all' ? '' : v)}>
          <SelectTrigger className={`w-full sm:w-[220px] rounded-sm h-9 text-sm ${filterPeriodo ? 'border-[#4338CA] text-[#4338CA]' : ''}`} data-testid="filter-addebiti-periodo"><SelectValue placeholder="Tutti i periodi" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti i periodi</SelectItem>
            {periodi.map(p => <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={sortBy || 'default'} onValueChange={(v) => setSortBy(v === 'default' ? '' : v)}>
          <SelectTrigger className="w-full sm:w-[240px] rounded-sm h-9 text-sm" data-testid="filter-addebiti-sort"><SelectValue placeholder="Ordina per..." /></SelectTrigger>
          <SelectContent>
            <SelectItem value="default">Scadenza (dalla più vecchia)</SelectItem>
            <SelectItem value="teacher">Insegnante · Scadenza · Alunno</SelectItem>
            <SelectItem value="student">Alunno · Scadenza</SelectItem>
            <SelectItem value="scadenza">Scadenza · Alunno</SelectItem>
          </SelectContent>
        </Select>
        {hasFilters && <Button variant="ghost" size="sm" onClick={clearFilters} className="h-9 text-sm text-[#52525B]" data-testid="clear-addebiti-filter"><X size={14} className="mr-1" />Pulisci</Button>}
        <div className="text-sm text-[#52525B] font-medium ml-auto" data-testid="addebiti-count">{total} {total === 1 ? 'addebito' : 'addebiti'}</div>
      </div>

      {/* Toolbar Assegna Ricevuta */}
      <div className="flex items-center gap-3 mb-4 p-3 bg-[#FAFAFA] border border-[#E4E4E7] rounded-sm flex-wrap animate-fade-in" data-testid="assegna-ricevuta-toolbar">
        <div className="flex items-center gap-2">
          <Receipt size={16} className="text-[#4338CA]" />
          <span className="text-xs font-semibold uppercase tracking-wider text-[#4338CA]">Assegna Ricevuta</span>
        </div>
        <div className="flex items-center gap-2">
          <Label htmlFor="nr-ricevuta" className="text-xs text-[#52525B]">Nr. Ricevuta</Label>
          <Input
            id="nr-ricevuta"
            type="text"
            value={nrRicevuta}
            onChange={(e) => setNrRicevuta(e.target.value)}
            placeholder="es. 2026/001"
            className="w-32 h-8 text-xs rounded-sm"
            data-testid="input-nr-ricevuta"
          />
        </div>
        <div className="flex items-center gap-2">
          <Label htmlFor="data-ricevuta" className="text-xs text-[#52525B]">Data</Label>
          <Input
            id="data-ricevuta"
            type="date"
            value={dataRicevuta}
            onChange={(e) => setDataRicevuta(e.target.value)}
            className="w-40 h-8 text-xs rounded-sm"
            data-testid="input-data-ricevuta"
          />
        </div>
        <Button
          type="button"
          size="sm"
          onClick={assegnaRicevuta}
          disabled={selectedCount === 0 || !nrRicevuta.trim() || !dataRicevuta || assegnando}
          className="rounded-sm bg-[#4338CA] text-white h-8 text-xs hover:bg-[#3730A3]"
          data-testid="btn-assegna-ricevuta"
        >
          <CheckSquare size={14} className="mr-1" />{assegnando ? 'Assegnazione...' : 'Assegna Ricevuta'}
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handlePrint}
          disabled={selectedCount === 0 || printing}
          className="rounded-sm h-8 text-xs border-[#2D2A4A] text-[#2D2A4A] bg-white hover:bg-[#EEF2FF]"
          data-testid="btn-print-addebiti"
          title="Stampa tabulato degli addebiti selezionati con totali"
        >
          <Printer size={14} className="mr-1" weight="bold" />{printing ? 'Preparazione...' : 'Stampa'}
        </Button>
        <div className="ml-auto flex items-center gap-4 text-sm">
          {selectedCount > 0 ? (
            <>
              <span className="text-[#4338CA] font-semibold" data-testid="selected-count">{selectedCount} selezionati</span>
              <span className="text-[#52525B]">
                <span className="uppercase tracking-wider text-[10px] mr-1">Parziale Alunno:</span>
                <span className="font-bold font-['Work_Sans'] text-[#2D2A4A]" data-testid="selected-total-alunno">{formatCurrency(selectedTotals.importo)}</span>
              </span>
              <span className="text-[#52525B]">
                <span className="uppercase tracking-wider text-xs mr-1">Parziale Dovuto Ins.:</span>
                <span className="font-bold font-['Work_Sans'] text-base text-[#4338CA]" data-testid="selected-total-dovuto-ins">{formatCurrency(selectedTotals.importo_insegnante)}</span>
              </span>
              <Button
                type="button"
                size="sm"
                onClick={resetSelezioni}
                disabled={selectedCount === 0}
                className="rounded-sm bg-red-600 text-white hover:bg-red-700 h-8 text-xs"
                data-testid="btn-reset-spunte"
                title="Azzera tutte le selezioni salvate"
              >
                <X size={14} className="mr-1" />Reset spunte
              </Button>
            </>
          ) : (
            <span className="text-[#A1A1AA]">Nessun addebito selezionato</span>
          )}
        </div>
      </div>

      <div className="animate-fade-in animate-delay-2 overflow-x-auto -mx-3 sm:mx-0 px-3 sm:px-0">
        {loading ? (
          <div className="text-center py-12 text-[#52525B]">Caricamento...</div>
        ) : rows.length === 0 ? (
          <div className="text-center py-12 bg-white border border-[#E4E4E7] rounded-sm">
            <CurrencyEur size={48} className="mx-auto text-[#E4E4E7] mb-4" />
            <p className="text-[#52525B]">{hasFilters ? 'Nessun addebito trovato' : 'Nessun addebito importato. Usa "Importa Addebiti" per iniziare.'}</p>
          </div>
        ) : (
          <div className="bg-white border border-[#E4E4E7] rounded-sm overflow-hidden">
            <table className="w-full text-xs" data-testid="addebiti-table">
              <thead><tr className="border-b border-[#E4E4E7] bg-[#FAFAFA]">
                <th className="text-left px-2 py-2 font-semibold uppercase tracking-wider text-[#52525B]">Cod. Alunno</th>
                <th className="text-left px-2 py-2 font-semibold uppercase tracking-wider text-[#52525B]">Alunno</th>
                <th className="text-left px-2 py-2 font-semibold uppercase tracking-wider text-[#52525B]">Insegnante</th>
                <th className="text-right px-2 py-2 font-semibold uppercase tracking-wider text-[#52525B]">Importo Alunno</th>
                <th className="text-center px-2 py-2 font-semibold uppercase tracking-wider text-[#52525B]">Anno Comp.</th>
                <th className="text-left px-2 py-2 font-semibold uppercase tracking-wider text-[#52525B]">Scadenza</th>
                <th className="text-left px-2 py-2 font-semibold uppercase tracking-wider text-[#52525B]">Descrizione</th>
                <th className="text-center px-2 py-2 w-8">
                  <input
                    type="checkbox"
                    checked={eligibleRows.length > 0 && eligibleRows.every(r => isSelected(r.id))}
                    ref={el => { if (el) el.indeterminate = eligibleRows.some(r => isSelected(r.id)) && !eligibleRows.every(r => isSelected(r.id)); }}
                    onChange={toggleSelectAll}
                    disabled={eligibleRows.length === 0}
                    className="rounded"
                    data-testid="addebiti-select-all"
                    title="Seleziona / Deseleziona tutti gli addebiti modificabili in questa pagina"
                  />
                </th>
                <th className="text-left px-2 py-2 font-semibold uppercase tracking-wider text-[#52525B]">Pagamento</th>
                <th className="text-right px-2 py-2 font-semibold uppercase tracking-wider text-[#52525B]">Dovuto Ins.</th>
                <th className="text-right px-2 py-2 font-semibold uppercase tracking-wider text-[#52525B]">Pagato Ins.</th>
                <th className="text-left px-2 py-2 font-semibold uppercase tracking-wider text-[#52525B]">Ricevuta</th>
              </tr></thead>
              <tbody>{rows.map(p => {
                const editable = canEditDovuto(p);
                const rowSelected = isSelected(p.id);
                return (
                <tr key={p.id} className={`border-b border-[#E4E4E7] ${rowSelected ? 'bg-[#DBEAFE] hover:bg-[#BFDBFE]' : 'hover:bg-[#FAFAFA]'}`} data-testid={`addebiti-row-${p.id}`}>
                  <td className="px-2 py-1.5 font-mono text-xs text-[#2D2A4A] font-semibold">{p.codice_alunno || '-'}</td>
                  <td className="px-2 py-1.5 font-medium uppercase">{p.student_name}</td>
                  <td className="px-2 py-1.5">{p.teacher_name}</td>
                  <td className="px-2 py-1.5 text-right font-medium font-['Work_Sans']">{formatCurrency(p.importo)}</td>
                  <td className="px-2 py-1.5 text-center font-['Work_Sans']">{p.anno_competenza || '-'}</td>
                  <td className="px-2 py-1.5 text-[#52525B]">{formatDate(p.data_scadenza)}</td>
                  <td className="px-2 py-1.5 text-[#52525B] max-w-[180px] truncate" title={p.descrizione}>{p.descrizione || '-'}</td>
                  <td className="px-2 py-1.5 text-center">
                    {editable ? (
                      <input type="checkbox" checked={rowSelected} onChange={() => toggleSelect(p)} className="rounded" data-testid={`addebiti-select-${p.id}`} />
                    ) : (
                      <span className="text-[#A1A1AA] text-[10px]" title="Non modificabile">–</span>
                    )}
                  </td>
                  <td className="px-2 py-1.5 text-[#52525B]">{formatDate(p.data_pagamento)}</td>
                  <td className="px-2 py-1.5 text-right">
                    {editable ? (
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        defaultValue={p.importo_insegnante ? Number(p.importo_insegnante).toFixed(2) : ''}
                        onBlur={(e) => {
                          const raw = e.target.value;
                          const newVal = raw === '' ? 0 : parseFloat(raw);
                          if (Number.isNaN(newVal)) { e.target.value = p.importo_insegnante ? Number(p.importo_insegnante).toFixed(2) : ''; return; }
                          const rounded = Math.round(newVal * 100) / 100;
                          if (rounded === (p.importo_insegnante || 0)) { e.target.value = rounded ? rounded.toFixed(2) : ''; return; }
                          e.target.value = rounded.toFixed(2);
                          updatePayment(p.id, { importo_insegnante: rounded });
                        }}
                        className="w-24 text-right border border-transparent hover:border-[#E4E4E7] focus:border-[#2D2A4A] rounded px-1.5 py-0.5 outline-none font-['Work_Sans'] font-medium bg-transparent"
                        data-testid={`edit-dovuto-${p.id}`}
                      />
                    ) : (
                      <span className="font-['Work_Sans'] text-[#3F3F46]">{p.importo_insegnante ? Number(p.importo_insegnante).toFixed(2) : <span className="text-[#A1A1AA]">-</span>}</span>
                    )}
                  </td>
                  <td className="px-2 py-1.5 text-right">
                    {editable ? (
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        key={`pag-${p.id}-${p.pagato_insegnante || 0}`}
                        defaultValue={p.pagato_insegnante ? Number(p.pagato_insegnante).toFixed(2) : ''}
                        onBlur={(e) => {
                          const raw = e.target.value;
                          const newVal = raw === '' ? 0 : parseFloat(raw);
                          if (Number.isNaN(newVal)) { e.target.value = p.pagato_insegnante ? Number(p.pagato_insegnante).toFixed(2) : ''; return; }
                          const rounded = Math.round(newVal * 100) / 100;
                          if (rounded === (p.pagato_insegnante || 0)) { e.target.value = rounded ? rounded.toFixed(2) : ''; return; }
                          e.target.value = rounded.toFixed(2);
                          updatePayment(p.id, { pagato_insegnante: rounded });
                        }}
                        className="w-24 text-right border border-transparent hover:border-[#E4E4E7] focus:border-[#2D2A4A] rounded px-1.5 py-0.5 outline-none font-['Work_Sans'] font-medium bg-transparent"
                        data-testid={`edit-pagato-ins-${p.id}`}
                      />
                    ) : (
                      <span className="text-[#A1A1AA]">-</span>
                    )}
                  </td>
                  <td className="px-2 py-1.5 text-[10px] text-[#52525B]">
                    {p.nr_ricevuta_ins ? (
                      <div>
                        <div className="font-semibold text-[#4338CA]">{p.nr_ricevuta_ins}</div>
                        <div>{formatDate(p.data_ricevuta_ins)}</div>
                      </div>
                    ) : <span className="text-[#A1A1AA]">-</span>}
                  </td>
                </tr>
              );})}</tbody>
            </table>
          </div>
        )}
      </div>
      {pages > 1 && (
        <div className="flex items-center justify-between mt-3">
          <div className="text-sm text-[#52525B]">Pagina {page} di {pages}</div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1} className="rounded-sm" data-testid="prev-addebiti-page"><CaretLeft size={14} className="mr-1" />Precedente</Button>
            <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(pages, p + 1))} disabled={page >= pages} className="rounded-sm" data-testid="next-addebiti-page">Successiva<CaretRight size={14} className="ml-1" /></Button>
          </div>
        </div>
      )}
    </>
  );
}
